import React, { Component } from 'react';
import { Form, Field, change } from 'redux-form';
import { Grid, Row, Col, Popover, OverlayTrigger, Label } from 'react-bootstrap';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { get } from 'lodash';
import { BRAND_WHITELABEL_UPDATE, BRAND_WHITELABEL_VIEW } from 'constants/capabilities';
import ProtectedComponent from 'hoc/ProtectedComponent';
import StyledLabel from 'components/atoms/inputs/Label';
import FormSectionHeading from 'components/atoms/form/FormSectionHeading';
import ButtonStandardPrimary from 'components/atoms/buttons/standard/Primary';
import CheckBoxSelect from 'components/atoms/form/CheckBoxSelect';
import TextInputAdmin from 'components/atoms/inputs/TextInputAdmin';
import LabelWithHelpOverlay from 'components/molecules/LabelWithHelpOverlay';
import InputTip from 'components/atoms/form/InputTip';
import { HelpIcon } from 'components/atoms/HelpIcon';
import { DisabledDiv } from 'components/atoms/form/UtilityDivs';
import { generateLabelCodePreview } from 'helpers/labelCode';

const StyledGrid = styled(Grid)`
     font-size: 1em;
     padding-top: 16px;
     margin-left: -15px;
     line-height: 16px;
     color: #000000;
`;

const FlexWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const labelCodeHelpText = (<Popover
    id="labelcode-help-text"
    placement="right"
    title="Label Codes"
>
    The label entered in this field will replace the default text used for eCPA objective
    reporting (i.e. eCPA Activities). This will NOT affect the data loaded in any way.
</Popover>);

const WhitelabelViewProtectedComponent = ProtectedComponent({
    requiredCapabilities: [BRAND_WHITELABEL_VIEW],
})(props => (<React.Fragment>{props.children}</React.Fragment>));

const WhitelabelFieldProtectedComponent = ProtectedComponent({
    requiredCapabilities: [BRAND_WHITELABEL_UPDATE],
    alwaysRender: true,
    onRenderProps: ({ props, check }) => ({ ...props, disabled: !check })
})(Field);

const whitelabelSubdomainText = (<Popover id="subdomain" placement="right" title="What are Whitelabel Subdomains?">
    This setting allows entities attached to this brand to be accessed from a whitelabeled portal styled and built
    for this brand. This is an advanced feature and should only be modified if you understand the effects. Any user
    associated with a brand with a whitelabel subdomain will only be allowed to login through this brand's whitelabel
    subdomain.
</Popover>);

const enforceSummaryOnlyDashboardViewHelpText = (<Popover
    id="enforce-summary-only-dashboard-view"
    placement="right"
    title="Summary Mode"
>
    This will disable links to any channel-specific pages from the dashboard summary view for an entity.
</Popover>);

const ecpaSummaryViewHelpText = (<Popover
    id="ecpa-summary-conversions"
    placement="right"
    title="Show eCPA on Summary Channel"
>
    Enabling this option will include eCPA conversions in the summary channel.
    This currently includes the Social and SEM channels as well.
</Popover>);

const showSocialExpandModulesHelpText = (<Popover
    id="show-social-expand-modules"
    placement="right"
    title="Show Facebook Expand Modules on Social Channel"
>
    Enabling this option will include all Facebook expand modules in the social
    channel broken out by campaign objective.
</Popover>);

const showTtdSitesSummaryHelpText = (
    <Popover
        id={'site-summary-data'}
        placement={'right'}
        title={'Show Channel Placement Distributions'}
    >
        Enabling this will show an additional channel breakdown table that shows impressions and completions for ATV and
        video ads broken out by the channels on which they are shown.
    </Popover>
);

class EditBrandOptionsForm extends Component {
    constructor(props) {
        super();
        this.state = {
            hideGGBranding: get(props.brand, 'meta.whitelabel.hideGGBranding'),
            disableGGBranding: !!get(props.brand, 'meta.whitelabel.subdomain'),
            initialHideValue: get(props.brand, 'meta.whitelabel.hideGGBranding')
        };
        this.onTextBlur = this.onTextBlur.bind(this);
    }

    onTextBlur(props) {
        const hasValue = !!props.currentTarget.value;

        if (hasValue) {
            this.props.dispatch(change('editBrandOptions', 'meta.whitelabel.hideGGBranding', true));
        } else {
            this.props.dispatch(change('editBrandOptions', 'meta.whitelabel.hideGGBranding', this.state.initialHideValue));
        }

        this.setState({
            ...this.state,
            disableGGBranding: hasValue,
        });
    }

    render() {
        const { pristine, selectedLabelCode } = this.props;
        return (
            <Form onSubmit={this.props.handleSubmit()} id="editBrandOptionsForm">
                <StyledGrid fluid>
                    <Row>
                        <Col xs={12} sm={6} md={5} lg={4}>
                            <FormSectionHeading marginTop={0}>TTD/DCM/DV360 Only</FormSectionHeading>
                            <Field
                                component={CheckBoxSelect}
                                label="Show Breakdown by Targeting Tactic"
                                name={'meta.showTargetingTactics'}
                                type="checkbox"
                            />
                            <br />
                            <Field
                                component={CheckBoxSelect}
                                label="Hide Budget Over Time Charts"
                                name={'meta.hideBudgetTimeCharts'}
                                type="checkbox"
                            />
                            <br />
                            <Field
                                component={CheckBoxSelect}
                                label="Include non-eCPA Activities"
                                name={'meta.showTotalActivities'}
                                type="checkbox"
                            />
                            <br />
                            <Field
                                component={CheckBoxSelect}
                                label='Use Activity Group Label from DCM'
                                name={'meta.useActivityGroupLabel'}
                                type="checkbox"
                            />
                            <FormSectionHeading>Show Data</FormSectionHeading>
                            <Field
                                component={CheckBoxSelect}
                                label="Show Conversion Revenue"
                                name="meta.showConversionRevenue"
                                type="checkbox"
                            />
                            <br />
                            <Field
                                component={CheckBoxSelect}
                                label="Show SEM Cost per Conversion"
                                name={'meta.showSemCpconv'}
                                type="checkbox"
                            />
                            <br />
                            <Field
                                component={CheckBoxSelect}
                                label="Show Social Cost per Conversion"
                                name={'meta.showSocialCpconv'}
                                type="checkbox"
                            />
                            <br />
                            <Field
                                component={CheckBoxSelect}
                                label="Show Facebook Demographic Data"
                                name={'meta.showFacebookDemographicData'}
                                type="checkbox"
                            />
                            <br />
                            <Field
                                component={CheckBoxSelect}
                                label="Show Facebook Expand Modules"
                                name={'meta.showSocialExpandModules'}
                                type="checkbox"
                                overlay={showSocialExpandModulesHelpText}
                            />
                            <br />
                            <Field
                                component={CheckBoxSelect}
                                label="Show eCPA on Summary Channel"
                                name={'meta.showeCPAonSummaryDashboard'}
                                type="checkbox"
                                overlay={ecpaSummaryViewHelpText}
                            />
                            <InputTip marginTop="-10px" marginBottom="-5px">Heads up, this includes SEM and Social conversions</InputTip>
                            <br />
                            <Field
                                component={CheckBoxSelect}
                                label={
                                    <React.Fragment>
                                        Show Video/ATV Channel Distributions
                                        <Label bsStyle="primary" style={{ marginLeft: '5px', verticalAlign: 'middle' }}>Beta</Label>
                                    </React.Fragment>
                                }
                                name={'meta.showTtdSitesSummary'}
                                type="checkbox"
                                overlay={showTtdSitesSummaryHelpText}
                            />
                            <br />
                            <FormSectionHeading>Additional Functionality</FormSectionHeading>
                            <Field
                                component={CheckBoxSelect}
                                label="Enable Objective Switching"
                                name={'meta.enableObjectiveSwitching'}
                                type="checkbox"
                            />
                            <br />
                            <Field
                                component={CheckBoxSelect}
                                label="Enable Summary Mode: Removes links to channel specific detail pages."
                                name="meta.enforceSummaryOnlyDashboardView"
                                type="checkbox"
                                overlay={enforceSummaryOnlyDashboardViewHelpText}
                            />
                            <FormSectionHeading>Branding</FormSectionHeading>
                            <DisabledDiv disablePointer={this.state.disableGGBranding}>
                                <Field
                                    component={CheckBoxSelect}
                                    label='Hide Goodway Group Branding'
                                    name={'meta.whitelabel.hideGGBranding'}
                                    type="checkbox"
                                />
                            </DisabledDiv>
                            <br />
                            <br />
                            <FlexWrapper>
                                <StyledLabel>Custom eCPA Label</StyledLabel>
                                <OverlayTrigger trigger="click" placement="right" overlay={labelCodeHelpText}>
                                    <HelpIcon padding={'1px'} />
                                </OverlayTrigger>
                                <br />
                            </FlexWrapper>
                            <Field
                                name="meta.programmaticObjective.labelCode"
                                autocomplete="nope"
                                placeholder={'E.g. Leads, Sales, Activities, etc.'}
                                required={false}
                                component={TextInputAdmin}
                            />
                            <InputTip marginBottom="10px">{generateLabelCodePreview(selectedLabelCode)}</InputTip>
                            <WhitelabelViewProtectedComponent>
                                <LabelWithHelpOverlay overlay={whitelabelSubdomainText}>
                                    Whitelabel Subdomain
                                </LabelWithHelpOverlay>
                                <WhitelabelFieldProtectedComponent
                                    component={TextInputAdmin}
                                    label='Whitelabel Subdomain'
                                    name={'meta.whitelabel.subdomain'}
                                    onChange={this.onTextBlur}
                                    normalize={value => (typeof value === 'string') && value.toLowerCase()}
                                    type="text"
                                    required={false}
                                    inputGroupTextAppend={`.${this.props.whitelabelDomain}`}
                                />
                                <InputTip marginBottom="-5px">Creating a Whitelabel Subdomain is not an automated process. Please allow one business day.</InputTip>
                            </WhitelabelViewProtectedComponent>
                        </Col>
                    </Row>
                    <br />
                    <ButtonStandardPrimary disabled={pristine} type="submit">Submit</ButtonStandardPrimary>
                </StyledGrid>
            </Form>
        );
    }
}

EditBrandOptionsForm.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    brand: PropTypes.object,
    whitelabelDomain: PropTypes.string,
    selectedLabelCode: PropTypes.string,
};

export default EditBrandOptionsForm;



// WEBPACK FOOTER //
// ./src/components/organisms/forms/EditBrandOptions.js